import { Fragment, useCallback, useEffect, useState } from 'react';
import {
  Brain, RefreshCw, Loader2, HeartPulse, GitBranch, Grid3x3, ListOrdered,
  CheckCircle2, XCircle, FileCheck, BookOpen, UserCog, ShieldAlert, Layers,
  ArrowDown, ArrowUp, Minus, ChevronDown, ChevronUp, AlertTriangle,
  ListTodo, Undo2, ShieldOff,
} from 'lucide-react';
import { adminLearningApi } from '../../services/api';

/* ── API shapes ──────────────────────────────────────────────────────── */

interface JobHealth {
  job: string;
  label: string;
  cadence: string;
  status: 'ok' | 'stale' | 'never';
  last_run_at: string | null;
  last_stats: Record<string, number> | null;
}
interface LearningHealth {
  jobs: JobHealth[];
  intake: {
    last_final_at: string | null;
    miner_cursor_at: string | null;
    unmined_backlog: number;
    quarantined_rows: number;
  };
}
interface EvolutionEvent {
  ts: string;
  kind: string;
  exam_category: string | null;
  summary: string;
  detail: string;
}
interface CategoryCoverage {
  exam_category: string;
  generations: number;
  finals: number;
  last_final_at: string | null;
  active_rules: number;
  candidate_rules: number;
  profile_engine: string | null;
  pending_proposals: number;
  error_signals: number;
  avg_edit_distance_recent: number | null;
  avg_edit_distance_prior: number | null;
}
interface ExemplarRef {
  edit_id: string;
  created_at: string | null;
  trigger: string;
  has_edits: boolean;
}
interface LearningContext {
  profile_engine: string | null;
  exemplars: ExemplarRef[];
  style_corrections_chars: number;
  style_config_present: boolean;
  language_config_present: boolean;
  template_name: string | null;
  classification_method: string | null;
  classification_confidence: number | null;
  active_rules?: number;
  candidate_rules?: number;
}
interface ReviewRule {
  id: string;
  exam_category: string;
  trigger_phrase: string;
  replacement_phrase: string;
  occurrences: number;
  kept: number;
  confidence: number;
  state: 'candidate' | 'active' | 'retracted';
  source: string;
  last_seen: string | null;
  retraction_reason: string | null;
}
interface GenerationRow {
  id: string;
  created_at: string;
  exam_category: string | null;
  pipeline: string;
  status: string;
  latency_ms: number | null;
  error_signals: number;
  learning_context: LearningContext | null;
  outcome: {
    captured: boolean;
    trigger: string | null;
    has_edits: boolean | null;
    edit_distance: number | null;
  };
}

/* ── helpers ─────────────────────────────────────────────────────────── */

function timeAgo(iso: string | null): string {
  if (!iso) return 'never';
  const ms = Date.now() - new Date(iso + (iso.endsWith('Z') ? '' : 'Z')).getTime();
  const min = Math.floor(ms / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const h = Math.floor(min / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

const JOB_STATUS_BADGE: Record<JobHealth['status'], string> = {
  ok: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  stale: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  never: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
};
const JOB_STATUS_LABEL: Record<JobHealth['status'], string> = {
  ok: 'running', stale: 'stalled', never: 'no heartbeat yet',
};

const EVENT_META: Record<string, { label: string; icon: typeof Layers; badge: string }> = {
  rule_candidate:     { label: 'Rule observed',      icon: Layers,      badge: 'bg-gray-100 dark:bg-surface-800 text-gray-600 dark:text-gray-300' },
  rule_activated:     { label: 'Rule activated',     icon: CheckCircle2, badge: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  rule_retracted:     { label: 'Rule retracted',     icon: XCircle,     badge: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
  template_proposed:  { label: 'Template proposed',  icon: FileCheck,   badge: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300' },
  template_approved:  { label: 'Template approved',  icon: CheckCircle2, badge: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  template_rejected:  { label: 'Template rejected',  icon: XCircle,     badge: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
  vocab_refreshed:    { label: 'Vocabulary refresh', icon: BookOpen,    badge: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  profile_created:    { label: 'Profile created',    icon: UserCog,     badge: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400' },
  intake_quarantined: { label: 'Intake quarantined', icon: ShieldAlert, badge: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' },
};

/** Diagnostic chips for one generation: separate "the engine had little to
 *  work with" (learning gap) from "inputs were rich but the report still
 *  needed heavy edits" (points at the LLM / report type). */
function diagnose(g: GenerationRow): { label: string; tone: 'red' | 'amber' | 'green' | 'gray' }[] {
  const chips: { label: string; tone: 'red' | 'amber' | 'green' | 'gray' }[] = [];
  const lc = g.learning_context;
  if (!lc) {
    chips.push({ label: 'inputs not recorded (pre-Aug 16 build)', tone: 'gray' });
  } else {
    if (!lc.exemplars || lc.exemplars.length === 0) chips.push({ label: 'no voice exemplars — learning gap', tone: 'amber' });
    if (!lc.profile_engine) chips.push({ label: 'no report profile — learning gap', tone: 'amber' });
    if (lc.profile_engine === 'legacy') chips.push({ label: 'legacy prompt (dynamic engine off)', tone: 'gray' });
    if (lc.style_corrections_chars === 0) chips.push({ label: 'no style corrections', tone: 'gray' });
    if (lc.classification_method && ['fallback'].includes(lc.classification_method)) {
      chips.push({ label: 'classifier fell back', tone: 'amber' });
    }
    const rich = (lc.exemplars?.length ?? 0) > 0 && !!lc.profile_engine && lc.profile_engine !== 'legacy';
    const heavyEdit = g.outcome.captured && (g.outcome.edit_distance ?? 0) > 300;
    if (rich && heavyEdit) chips.push({ label: 'rich inputs, heavy edits — likely model/report-type limit', tone: 'red' });
    if (rich && g.outcome.captured && !heavyEdit) chips.push({ label: 'inputs healthy', tone: 'green' });
  }
  if (g.error_signals > 0) chips.push({ label: `${g.error_signals} error signal${g.error_signals > 1 ? 's' : ''}`, tone: 'red' });
  return chips;
}

const CHIP_TONE: Record<'red' | 'amber' | 'green' | 'gray', string> = {
  red: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  amber: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  green: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  gray: 'bg-gray-100 dark:bg-surface-800 text-gray-600 dark:text-gray-300',
};

/* ── component ───────────────────────────────────────────────────────── */

export default function Learning({ days }: { days: number }) {
  const [health, setHealth] = useState<LearningHealth | null>(null);
  const [events, setEvents] = useState<EvolutionEvent[]>([]);
  const [categories, setCategories] = useState<CategoryCoverage[]>([]);
  const [generations, setGenerations] = useState<GenerationRow[]>([]);
  const [genCategory, setGenCategory] = useState<string>('');
  const [expandedGen, setExpandedGen] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rules, setRules] = useState<ReviewRule[]>([]);
  const [ruleState, setRuleState] = useState<'candidate' | 'active' | 'retracted'>('candidate');
  const [ruleCategory, setRuleCategory] = useState<string>('');
  const [rulesLoading, setRulesLoading] = useState(false);
  const [actingRuleId, setActingRuleId] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [h, e, c, g] = await Promise.all([
        adminLearningApi.health(),
        adminLearningApi.evolution(days),
        adminLearningApi.categories(days),
        adminLearningApi.generations({ limit: 30, exam_category: genCategory || undefined }),
      ]);
      setHealth(h);
      setEvents(e.events);
      setCategories(c.categories);
      setGenerations(g.generations);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load learning data');
    } finally {
      setLoading(false);
    }
  }, [days, genCategory]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const fetchRules = useCallback(async () => {
    setRulesLoading(true);
    try {
      const r = await adminLearningApi.rules({ state: ruleState, limit: 500 });
      setRules(r.rules);
    } catch {
      /* surfaced by the main error strip on next full refresh */
    } finally {
      setRulesLoading(false);
    }
  }, [ruleState]);

  useEffect(() => { fetchRules(); }, [fetchRules]);

  const actOnRule = async (rule: ReviewRule, action: 'activate' | 'dismiss' | 'restore') => {
    if (action === 'dismiss' && rule.state === 'active') {
      if (!window.confirm(`Retract this active rule?\n\n“${rule.trigger_phrase}” → “${rule.replacement_phrase || '(delete)'}”\n\nIt will stop being applied and won't come back unless restored.`)) return;
    }
    setActingRuleId(rule.id);
    try {
      if (action === 'activate') await adminLearningApi.activateRule(rule.id);
      else if (action === 'dismiss') await adminLearningApi.dismissRule(rule.id);
      else await adminLearningApi.restoreRule(rule.id);
      await fetchRules();
    } finally {
      setActingRuleId(null);
    }
  };

  const ruleCategories = Array.from(new Set(rules.map((r) => r.exam_category))).sort();
  const visibleRules = ruleCategory ? rules.filter((r) => r.exam_category === ruleCategory) : rules;

  if (loading && !health) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-400 text-sm">
        <Loader2 size={20} className="animate-spin text-primary-500 mr-2" /> Loading learning engine…
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Brain size={18} className="text-primary-600 dark:text-primary-400" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Learning engine</h2>
          <span className="text-xs text-gray-500 dark:text-gray-400">how the system is adapting to your reporting — and where it can't yet</span>
        </div>
        <button
          onClick={fetchAll}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-primary-600 hover:bg-primary-700 text-white rounded-md transition-colors disabled:opacity-40"
          disabled={loading}
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {error && (
        <div className="text-xs text-red-600 bg-red-50 dark:bg-red-900/20 rounded px-2 py-1.5">{error}</div>
      )}

      {/* ── Rule review ─────────────────────────────────────────────── */}
      <section className="bg-white dark:bg-surface-900 rounded-lg border border-gray-200 dark:border-surface-800">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-surface-800 flex items-center gap-2 flex-wrap">
          <ListTodo size={14} className="text-primary-600 dark:text-primary-400" />
          <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Rule review</h4>
          {ruleState === 'candidate' && rules.length > 0 && (
            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-surface-800 text-gray-600 dark:text-gray-300">
              {rules.length} candidate{rules.length === 1 ? '' : 's'}
            </span>
          )}
          <span className="px-1.5 py-0.5 text-[11px] rounded bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400">optional</span>
          <div className="flex items-center gap-1 ml-auto">
            {(['candidate', 'active', 'retracted'] as const).map((s) => (
              <button
                key={s}
                onClick={() => setRuleState(s)}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  ruleState === s
                    ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                    : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-surface-800'
                }`}
              >
                {s === 'candidate' ? 'Candidates' : s === 'active' ? 'Active' : 'Dismissed'}
              </button>
            ))}
            <select
              value={ruleCategory}
              onChange={(e) => setRuleCategory(e.target.value)}
              className="ml-2 px-2 py-1.5 text-sm bg-white dark:bg-surface-800 border border-gray-300 dark:border-surface-700 rounded-md text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              <option value="">All exam types</option>
              {ruleCategories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
        <div className="px-4 pt-2 text-[11px] text-gray-400">
          Nothing here needs your action — the engine runs itself, activating a rule once a correction is seen 3+ times and kept in ≥80% of finals.
          Use this box only if you want to fast-track a rule you like or veto one you don't; your decisions always override the automatics.
        </div>
        {rulesLoading && rules.length === 0 ? (
          <div className="p-4 text-sm text-gray-400"><Loader2 size={14} className="animate-spin inline mr-1 text-primary-500" /> Loading rules…</div>
        ) : visibleRules.length === 0 ? (
          <p className="text-gray-400 text-sm text-center py-6">
            {ruleState === 'candidate' ? 'No rules waiting for review.' : ruleState === 'active' ? 'No active rules.' : 'No dismissed rules.'}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-surface-800">
                  <th className="px-4 py-2 font-medium">Exam type</th>
                  <th className="px-3 py-2 font-medium">The AI writes → he changes it to</th>
                  <th className="px-3 py-2 font-medium text-right">Evidence</th>
                  <th className="px-3 py-2 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleRules.map((r) => (
                  <tr key={r.id} className="border-b border-gray-100 dark:border-surface-800/60 hover:bg-gray-50 dark:hover:bg-surface-800/50">
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      <span className="px-1.5 py-0.5 text-[11px] rounded bg-gray-100 dark:bg-surface-800 text-gray-600 dark:text-gray-300">{r.exam_category}</span>
                    </td>
                    <td className="px-3 py-2.5 text-gray-900 dark:text-white">
                      <span className="line-through text-gray-500">{r.trigger_phrase}</span>
                      {' → '}
                      {r.replacement_phrase
                        ? <span className="text-green-700 dark:text-green-400">{r.replacement_phrase}</span>
                        : <em className="text-gray-500">(remove it)</em>}
                      {r.state === 'retracted' && r.retraction_reason && (
                        <div className="text-[11px] text-gray-400 mt-0.5">{r.retraction_reason}</div>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-right whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">
                      seen {r.occurrences}× · kept {Math.round(r.confidence * 100)}%
                      {r.source === 'manual' && <span className="ml-1 px-1.5 py-0.5 text-[11px] rounded bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300">manual</span>}
                    </td>
                    <td className="px-3 py-2.5 text-right whitespace-nowrap">
                      {r.state === 'candidate' && (
                        <>
                          <button
                            onClick={() => actOnRule(r, 'activate')}
                            disabled={actingRuleId === r.id}
                            className="inline-flex items-center gap-1.5 px-2 py-1 text-xs border border-green-300 dark:border-green-800 text-green-600 dark:text-green-400 rounded hover:bg-green-50 dark:hover:bg-green-900/20 disabled:opacity-40 mr-2"
                          >
                            <CheckCircle2 size={12} /> Make active
                          </button>
                          <button
                            onClick={() => actOnRule(r, 'dismiss')}
                            disabled={actingRuleId === r.id}
                            className="inline-flex items-center gap-1.5 px-2 py-1 text-xs border border-red-300 dark:border-red-800 text-red-600 dark:text-red-400 rounded hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-40"
                          >
                            <XCircle size={12} /> Dismiss
                          </button>
                        </>
                      )}
                      {r.state === 'active' && (
                        <button
                          onClick={() => actOnRule(r, 'dismiss')}
                          disabled={actingRuleId === r.id}
                          className="inline-flex items-center gap-1.5 px-2 py-1 text-xs border border-red-300 dark:border-red-800 text-red-600 dark:text-red-400 rounded hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-40"
                        >
                          <ShieldOff size={12} /> Retract
                        </button>
                      )}
                      {r.state === 'retracted' && (
                        <button
                          onClick={() => actOnRule(r, 'restore')}
                          disabled={actingRuleId === r.id}
                          className="inline-flex items-center gap-1.5 px-2 py-1 text-xs border border-gray-300 dark:border-surface-700 rounded text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-surface-800 transition-colors disabled:opacity-40"
                        >
                          <Undo2 size={12} /> Restore
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ── Pipeline health ─────────────────────────────────────────── */}
      {health && (
        <section>
          <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2 flex items-center gap-1.5">
            <HeartPulse size={13} /> Pipeline health
          </h4>
          <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))' }}>
            {health.jobs.map((j) => (
              <div key={j.job} className="bg-white dark:bg-surface-900 rounded-lg border border-gray-200 dark:border-surface-800 p-4">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-gray-900 dark:text-white">{j.label}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${JOB_STATUS_BADGE[j.status]}`}>
                    {JOB_STATUS_LABEL[j.status]}
                  </span>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">{j.cadence} · last run {timeAgo(j.last_run_at)}</p>
                {j.last_stats && (
                  <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1.5 truncate" title={JSON.stringify(j.last_stats)}>
                    {Object.entries(j.last_stats).filter(([, v]) => typeof v === 'number' && v > 0).map(([k, v]) => `${k.replace(/_/g, ' ')}: ${v}`).join(' · ') || 'last run: nothing to do'}
                  </p>
                )}
              </div>
            ))}
            <div className="bg-white dark:bg-surface-900 rounded-lg border border-gray-200 dark:border-surface-800 p-4">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-gray-900 dark:text-white">Edit intake</span>
                {health.intake.unmined_backlog > 25 ? (
                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400">backlog</span>
                ) : (
                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">flowing</span>
                )}
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">last final captured {timeAgo(health.intake.last_final_at)}</p>
              <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1.5">
                {health.intake.unmined_backlog} awaiting mining · {health.intake.quarantined_rows} quarantined snapshots excluded
              </p>
            </div>
          </div>
        </section>
      )}

      {/* ── Category coverage ───────────────────────────────────────── */}
      <section className="bg-white dark:bg-surface-900 rounded-lg border border-gray-200 dark:border-surface-800">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-surface-800 flex items-center gap-1.5">
          <Grid3x3 size={14} className="text-gray-400" />
          <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">What the engine knows, per exam type</h4>
          <span className="text-[11px] text-gray-400 ml-auto">a busy exam type with no finals / profile / rules is a learning gap, not a model failure</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-surface-800">
                <th className="px-4 py-2 font-medium">Exam type</th>
                <th className="px-3 py-2 font-medium text-right">Reports ({days}d)</th>
                <th className="px-3 py-2 font-medium text-right">Your finals</th>
                <th className="px-3 py-2 font-medium text-right">Rules</th>
                <th className="px-3 py-2 font-medium">Profile</th>
                <th className="px-3 py-2 font-medium text-right">Errors</th>
                <th className="px-3 py-2 font-medium text-right">Edit trend</th>
                <th className="px-3 py-2 font-medium">Gaps</th>
              </tr>
            </thead>
            <tbody>
              {categories.map((c) => {
                const gap: string[] = [];
                if (c.generations > 0 && c.finals === 0) gap.push('no finals yet');
                if (c.generations > 0 && !c.profile_engine) gap.push('no profile');
                if (c.generations > 3 && c.active_rules === 0 && c.candidate_rules === 0) gap.push('no rules');
                const trendDelta = c.avg_edit_distance_recent !== null && c.avg_edit_distance_prior !== null
                  ? c.avg_edit_distance_recent - c.avg_edit_distance_prior : null;
                return (
                  <tr key={c.exam_category} className="border-b border-gray-100 dark:border-surface-800/60 hover:bg-gray-50 dark:hover:bg-surface-800/50">
                    <td className="px-4 py-2.5 text-gray-900 dark:text-white">{c.exam_category}</td>
                    <td className="px-3 py-2.5 text-right text-gray-700 dark:text-gray-300">{c.generations}</td>
                    <td className="px-3 py-2.5 text-right text-gray-700 dark:text-gray-300">
                      {c.finals}
                      {c.last_final_at && <span className="text-[11px] text-gray-400 ml-1">({timeAgo(c.last_final_at)})</span>}
                    </td>
                    <td className="px-3 py-2.5 text-right text-gray-700 dark:text-gray-300">
                      {c.active_rules}
                      {c.candidate_rules > 0 && <span className="text-[11px] text-gray-400 ml-1">+{c.candidate_rules} cand.</span>}
                    </td>
                    <td className="px-3 py-2.5">
                      {c.profile_engine ? (
                        <span className={`px-1.5 py-0.5 text-[11px] rounded ${c.profile_engine === 'dynamic'
                          ? 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400'
                          : 'bg-gray-100 dark:bg-surface-800 text-gray-600 dark:text-gray-300'}`}>
                          {c.profile_engine}
                        </span>
                      ) : <span className="text-gray-400 text-xs">—</span>}
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      {c.error_signals > 0
                        ? <span className="text-red-600 dark:text-red-400">{c.error_signals}</span>
                        : <span className="text-gray-400">0</span>}
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      {trendDelta === null ? (
                        <span className="text-gray-400 text-xs">—</span>
                      ) : trendDelta < -20 ? (
                        <span className="text-green-600 dark:text-green-400 inline-flex items-center gap-0.5 text-xs"><ArrowDown size={12} />{Math.abs(trendDelta)} fewer</span>
                      ) : trendDelta > 20 ? (
                        <span className="text-red-600 dark:text-red-400 inline-flex items-center gap-0.5 text-xs"><ArrowUp size={12} />{trendDelta} more</span>
                      ) : (
                        <span className="text-gray-500 inline-flex items-center gap-0.5 text-xs"><Minus size={12} />steady</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      {gap.length === 0
                        ? <span className="text-[11px] text-gray-400">covered</span>
                        : gap.map((gTxt) => (
                          <span key={gTxt} className="px-1.5 py-0.5 text-[11px] rounded bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400 mr-1">{gTxt}</span>
                        ))}
                    </td>
                  </tr>
                );
              })}
              {categories.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400 text-sm">No activity in this window</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* ── Recent reports: inputs → outcome ────────────────────────── */}
      <section className="bg-white dark:bg-surface-900 rounded-lg border border-gray-200 dark:border-surface-800">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-surface-800 flex items-center gap-1.5">
          <ListOrdered size={14} className="text-gray-400" />
          <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Recent reports — what the engine gave each one</h4>
          <select
            value={genCategory}
            onChange={(e) => setGenCategory(e.target.value)}
            className="ml-auto px-2 py-1.5 text-sm bg-white dark:bg-surface-800 border border-gray-300 dark:border-surface-700 rounded-md text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-primary-500"
          >
            <option value="">All exam types</option>
            {categories.map((c) => <option key={c.exam_category} value={c.exam_category}>{c.exam_category}</option>)}
          </select>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-surface-800">
                <th className="px-4 py-2 font-medium">When</th>
                <th className="px-3 py-2 font-medium">Exam type</th>
                <th className="px-3 py-2 font-medium text-right">Exemplars</th>
                <th className="px-3 py-2 font-medium">Profile</th>
                <th className="px-3 py-2 font-medium text-right">Edits made</th>
                <th className="px-3 py-2 font-medium">Reading</th>
                <th className="px-2 py-2" />
              </tr>
            </thead>
            <tbody>
              {generations.map((g) => {
                const lc = g.learning_context;
                const expanded = expandedGen === g.id;
                const chips = diagnose(g);
                return (
                  <Fragment key={g.id}>
                    <tr
                      onClick={() => setExpandedGen(expanded ? null : g.id)}
                      className={`cursor-pointer border-b border-gray-100 dark:border-surface-800/60 hover:bg-gray-50 dark:hover:bg-surface-800/50 ${expanded ? 'bg-primary-50/60 dark:bg-primary-900/10' : ''}`}
                    >
                      <td className="px-4 py-2.5 text-gray-700 dark:text-gray-300 whitespace-nowrap">{new Date(g.created_at + 'Z').toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                      <td className="px-3 py-2.5 text-gray-900 dark:text-white">{g.exam_category ?? '—'}</td>
                      <td className="px-3 py-2.5 text-right text-gray-700 dark:text-gray-300">{lc ? (lc.exemplars?.length ?? 0) : '·'}</td>
                      <td className="px-3 py-2.5 text-gray-700 dark:text-gray-300">{lc ? (lc.profile_engine ?? 'none') : '·'}</td>
                      <td className="px-3 py-2.5 text-right text-gray-700 dark:text-gray-300">
                        {g.outcome.captured
                          ? (g.outcome.has_edits ? `${g.outcome.edit_distance ?? '?'} chars` : 'accepted as-is')
                          : <span className="text-gray-400 text-xs">not captured</span>}
                      </td>
                      <td className="px-3 py-2.5">
                        {chips.slice(0, 2).map((ch) => (
                          <span key={ch.label} className={`px-1.5 py-0.5 text-[11px] rounded mr-1 ${CHIP_TONE[ch.tone]}`}>{ch.label}</span>
                        ))}
                      </td>
                      <td className="px-2 py-2.5 text-gray-400">{expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</td>
                    </tr>
                    {expanded && (
                      <tr className="border-b border-gray-100 dark:border-surface-800/60 bg-gray-50 dark:bg-surface-850">
                        <td colSpan={7} className="px-6 py-3">
                          <div className="grid gap-4 text-xs" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))' }}>
                            <div>
                              <h5 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Prompt inputs</h5>
                              {lc ? (
                                <ul className="space-y-1 text-gray-700 dark:text-gray-300">
                                  <li>Template: {lc.template_name ?? 'none'}</li>
                                  <li>Classified by {lc.classification_method ?? '—'}{lc.classification_confidence != null ? ` (${Math.round(lc.classification_confidence * 100)}%)` : ''}</li>
                                  <li>Style corrections: {lc.style_corrections_chars > 0 ? `${lc.style_corrections_chars} chars` : 'none'}</li>
                                  <li>Style / language config: {lc.style_config_present ? 'yes' : 'no'} / {lc.language_config_present ? 'yes' : 'no'}</li>
                                  {lc.active_rules !== undefined && <li>Renderer rules: {lc.active_rules} active, {lc.candidate_rules} candidate</li>}
                                </ul>
                              ) : (
                                <p className="text-gray-400">Not recorded — this report predates input tracking (16 Aug 2026).</p>
                              )}
                            </div>
                            <div>
                              <h5 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Voice exemplars used</h5>
                              {lc && lc.exemplars?.length ? (
                                <ul className="space-y-1 text-gray-700 dark:text-gray-300">
                                  {lc.exemplars.map((ex) => (
                                    <li key={ex.edit_id} className="flex items-center gap-1.5">
                                      <FileCheck size={12} className="text-teal-600 dark:text-teal-400" />
                                      final from {ex.created_at ? new Date(ex.created_at + 'Z').toLocaleDateString() : '?'}
                                      <span className="px-1.5 py-0.5 text-[11px] rounded bg-gray-100 dark:bg-surface-800 text-gray-600 dark:text-gray-300">{ex.trigger}</span>
                                      {!ex.has_edits && <span className="text-[11px] text-gray-400">(unedited)</span>}
                                    </li>
                                  ))}
                                </ul>
                              ) : (
                                <p className="text-gray-400">{lc ? 'None — the model had no examples of your reports for this exam type.' : '—'}</p>
                              )}
                            </div>
                            <div>
                              <h5 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Reading</h5>
                              <div className="flex flex-wrap gap-1">
                                {chips.map((ch) => (
                                  <span key={ch.label} className={`px-1.5 py-0.5 text-[11px] rounded ${CHIP_TONE[ch.tone]}`}>{ch.label}</span>
                                ))}
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
              {generations.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400 text-sm">No generations yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* ── Evolution timeline ──────────────────────────────────────── */}
      <section className="bg-white dark:bg-surface-900 rounded-lg border border-gray-200 dark:border-surface-800">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-surface-800 flex items-center gap-1.5">
          <GitBranch size={14} className="text-gray-400" />
          <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Engine change log</h4>
          <span className="text-[11px] text-gray-400 ml-auto">every change the learning engine made, newest first</span>
        </div>
        <div className="p-4 max-h-96 overflow-y-auto">
          {events.length === 0 ? (
            <p className="text-gray-400 text-sm text-center py-6">No engine changes in this window</p>
          ) : (
            <ol className="space-y-2">
              {events.map((ev, i) => {
                const meta = EVENT_META[ev.kind] ?? { label: ev.kind, icon: AlertTriangle, badge: 'bg-gray-100 dark:bg-surface-800 text-gray-600 dark:text-gray-300' };
                const Icon = meta.icon;
                return (
                  <li key={`${ev.ts}-${i}`} className="flex items-start gap-2 text-sm">
                    <span className="text-[11px] text-gray-400 w-20 shrink-0 pt-0.5 whitespace-nowrap">
                      {new Date(ev.ts + 'Z').toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                    </span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium inline-flex items-center gap-1 shrink-0 ${meta.badge}`}>
                      <Icon size={12} /> {meta.label}
                    </span>
                    {ev.exam_category && (
                      <span className="px-1.5 py-0.5 text-[11px] rounded bg-gray-100 dark:bg-surface-800 text-gray-600 dark:text-gray-300 shrink-0">{ev.exam_category}</span>
                    )}
                    <span className="text-gray-700 dark:text-gray-300 min-w-0 truncate" title={ev.detail || ev.summary}>{ev.summary}</span>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </section>
    </div>
  );
}
